/**
 * Self-voiced speech engine.
 *
 * The original Galaxy Ranger ships SAPI-style pre-recorded WAV samples for
 * every menu item, command-post entry, and status callout. Where one exists
 * the audio engine plays it directly; this module is the *fallback* for
 * dynamic content (numbers, coordinates, custom messages) that the original
 * synthesised at runtime via SAPI.
 *
 * We use the browser's `speechSynthesis` API for that fallback. To keep
 * the experience self-voiced (so the player doesn't need a screen reader
 * running), we do NOT write to any ARIA live region — the visible UI is
 * hidden from accessibility tools via aria-hidden.
 */

export class SpeechEngine {
  private synthAvailable: boolean;
  private rate = 1.15;
  private voice: SpeechSynthesisVoice | null = null;

  constructor() {
    this.synthAvailable = typeof window !== "undefined" && "speechSynthesis" in window;
    if (this.synthAvailable) {
      const pick = () => this.pickVoice();
      pick();
      window.speechSynthesis.addEventListener("voiceschanged", pick);
    }
  }

  private pickVoice(): void {
    if (!this.synthAvailable) return;
    const voices = window.speechSynthesis.getVoices();
    const score = (v: SpeechSynthesisVoice): number => {
      let s = 0;
      if (v.lang.startsWith("en")) s += 10;
      if (v.localService) s += 5;
      if (v.default) s += 3;
      return s;
    };
    const best = [...voices].sort((a, b) => score(b) - score(a))[0];
    if (best) this.voice = best;
  }

  setRate(rate: number): void {
    this.rate = rate;
  }

  /** Synthesise `text` via the browser's TTS, cancelling any prior utterance. */
  say(text: string): void {
    if (!this.synthAvailable || !text) return;
    try {
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = this.rate;
      if (this.voice) utt.voice = this.voice;
      window.speechSynthesis.speak(utt);
    } catch {
      /* ignore */
    }
  }

  cancel(): void {
    if (this.synthAvailable) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
  }
}
